import { useCallback, useEffect, useRef, useState } from 'react';
import { EarconName, useEarcon } from '../../hooks/useEarcon';
import { useMounted } from '../../hooks/useMounted';
import { addBundle } from '../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../locale/formatting';
import { useBrowserFormatter } from '../../locale/formatting/BrowserFormatter';
import { isArSaLocale, toLatinNumberingSystemLocale } from '../../locale/localeUtils';
import { getTextLocalizer, useTextLocalizer } from '../../locale/useTextLocalizer';
import bundle from './NovaComponents-Input-gui-icu.properties';
addBundle(bundle);
let localize;
getTextLocalizer('NovaComponents-Input-gui-icu').then(localizer => {
  localize = localizer;
});

/**
 * validate text input and return isError,status,message
 * @param props
 */
export const validateTextInput = props => {
  const localizer = props.localizer ?? localize;
  let isError = false,
    status,
    message;
  if (props.isRequired && props.value.trim() === '') {
    isError = true;
    status = 'alert';
    message = localizer('required.error.msg');
  } else if (props.value !== '' && props.value.length < props.minLength) {
    const minLen = props.minLength.toString();
    isError = true;
    status = 'alert';
    message = localizer('minlength.error.msg', {
      minlength: minLen
    });
  }
  return {
    isError,
    status,
    message
  };
};

/**
 * This hook provide functionality to handle events such as onChange,onBlur
 * & onCancel with empty initial value.
 * For those who doesn't wish to use this,
 * @function validateTextInput is available for consumption
 * @param props
 *
 */
export const useInputValidationAdapter = props => {
  const earcon = useEarcon({
    source: EarconName.InputFull
  });
  const [value, setValue] = useState(props.initialValue ?? '');
  const [errors, setErrors] = useState({
    status: undefined,
    message: ''
  });
  const localizer = useTextLocalizer('NovaComponents-Input-gui-icu');
  const mountedRef = useMounted();
  const handleChange = useCallback(event => {
    const liveValue = event.value;
    setValue(liveValue);
    setErrors({
      status: undefined,
      message: undefined
    });
  }, []);
  const handleCommit = useCallback(e => {
    // setTimeout is needed here to work with FocusLock.
    setTimeout(() => {
      const result = validateTextInput({
        value,
        localizer,
        ...props
      });
      if (!mountedRef.current) return;
      result.isError ? setErrors({
        status: result.status,
        message: result.message
      }) : setErrors({
        status: undefined,
        message: undefined
      });
      if (result.status === 'alert' && e?.reason === 'enter') earcon.play();
    }, 0);
  }, [earcon, localizer, mountedRef, props, value]);
  const handleBlur = handleCommit;
  const handleCancel = useCallback(() => {
    setValue('');
    setErrors({
      status: undefined,
      message: undefined
    });
  }, []);
  return {
    handleChange,
    value,
    setValue,
    errors,
    handleBlur,
    handleCancel,
    handleCommit
  };
};

/**
 * This hook provide functionality to handle events such as onChange,onBlur
 * & onCancel with empty initial value.
 * For those who doesn't wish to use this,
 * @function validateRangeInput is available for consumption
 * @param props
 *
 */
export const useNumericValidationAdapter = props => {
  const earcon = useEarcon({
    source: EarconName.InputFull
  });
  const locale = useFormatLocale();
  const formatter = useBrowserFormatter(locale);
  const initialValue = props.initialValue ?? '';
  const [value, setValue] = useState(initialValue);
  const [rawValue, setRawValue] = useState(props.initialValue ? Number(props.initialValue) : null);
  const [errors, setErrors] = useState({
    status: undefined,
    message: ''
  });
  const [currentLocale, setCurrentLocale] = useState('');
  const mountedRef = useMounted();
  const handleChange = event => {
    const liveValue = event.value;
    const [decimalSeparator] = getNumberSeparators(locale);
    const decimalValue = liveValue.lastIndexOf(decimalSeparator) !== -1 ? liveValue.slice(liveValue.lastIndexOf(decimalSeparator) + 1) : '';
    const result = props.validateInputFunction(liveValue, locale, props.isRequired, props.allowIntegersOnly, true, props.minLength, props.numberFormatOptions, props.parseAndFormatFunction);
    setRawValue(result.values.rawValue);
    result.isError ? setErrors({
      status: 'alert',
      message: result.message
    }) : setErrors({
      status: undefined,
      message: undefined
    });
    if (decimalValue !== '' && props.numberFormatOptions?.maximumFractionDigits && decimalValue.length > props.numberFormatOptions?.maximumFractionDigits) {
      earcon.play();
    } else if (result.status === 'alert') {
      earcon.play();
      setValue(liveValue);
    } else setValue(liveValue);
  };
  const handleCommit = e => {
    return handleBlurAndFocus(false, e);
  };
  const handleBlurAndFocus = (focused, e) => {
    // setTimeout is needed here to work with FocusLock.
    setTimeout(() => {
      const result = props.validateInputFunction(value, locale, props.isRequired, props.allowIntegersOnly, false, props.minLength, props.numberFormatOptions, props.parseAndFormatFunction);
      if (!mountedRef.current) return;
      if (result.isError) {
        if (e?.reason === 'enter') earcon.play();
        setValue(value);
      } else if (value === '') {
        setValue('');
      } else if (!focused && !props.disableAutoFormatting) {
        setValue(result.values.formatValue);
      } else if (focused && !props.disableAutoFormatting) {
        const separators = getNumberSeparators(locale);
        const formatValueWithoutGroupingSeparator = result.values.formatValue.replace(new RegExp(`[${separators[1]}]`, 'gu'), '');
        setValue(formatValueWithoutGroupingSeparator);
      } else {
        setValue(value);
        setRawValue(result.values.rawValue);
      }
      result.isError ? setErrors({
        status: 'alert',
        message: result.message
      }) : setErrors({
        status: undefined,
        message: undefined
      });
    }, 0);
  };
  const handleFocus = () => {
    return handleBlurAndFocus(true);
  };
  const handleBlur = handleCommit;
  const handleCancel = () => {
    setValue('');
    setErrors({
      status: undefined,
      message: undefined
    });
    setRawValue(null);
  };
  const revalidateRef = useRef();
  const isInitializedRef = useRef(false);
  if (!isArSaLocale(locale)) revalidateRef.current = handleBlur;
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      return;
    }
    revalidateRef.current?.();
  }, [props.isRequired, props.allowIntegersOnly, props.minLength, props.numberFormatOptions?.maximumFractionDigits, props.numberFormatOptions?.minimumFractionDigits]);
  useEffect(() => {
    if (!props.initialValue || errors.status || locale === currentLocale || props.disableAutoFormatting) return;
    setCurrentLocale(locale);
    const result = props.parseAndFormatFunction ? props.parseAndFormatFunction(rawValue ? rawValue?.toString() : props.initialValue) : parseAndFormatInput(rawValue ? formatter.formatNumeric(rawValue, undefined, props.numberFormatOptions) : formatter.formatNumeric(Number(props.initialValue)), locale, props.numberFormatOptions);
    if (result && !isNaN(parseFloat(result.formatValue))) {
      setValue(result.formatValue);
      setRawValue(result.rawValue);
    }
  }, [locale, rawValue, currentLocale, errors.status, props, formatter]);
  return {
    handleChange,
    value,
    errors,
    handleBlur,
    handleCancel,
    handleCommit,
    handleFocus
  };
};
const getDefaultLocale = locale => locale ? toLatinNumberingSystemLocale(locale) : 'en-US';
/**
 *
 * @param inputValue
 * @param applocale
 * @param numberFormatOptions
 * This function is for parsing numeric Input and
 * formatting it with grouping Separator as per locale.
 * @returns formatted value, raw value
 */
export const parseAndFormatInput = (inputValue, applocale, numberFormatOptions, formatter, isLive = true) => {
  const isValidNumberFormat = checkValidNumberFormat(inputValue, applocale, isLive);
  if (!isValidNumberFormat) {
    return {
      rawValue: NaN,
      formatValue: NaN.toString()
    };
  }
  const defaultLocale = getDefaultLocale(applocale);
  const defaultFormatter = new Intl.NumberFormat(defaultLocale, {
    useGrouping: true,
    ...numberFormatOptions
  });
  const locale = defaultFormatter.resolvedOptions().locale;
  const [decimalSeparator, groupingSeparator] = getNumberSeparators(locale);
  const patternDecimal = new RegExp(`[${decimalSeparator}]`, 'gu');
  const patternGroup = new RegExp(`[${groupingSeparator}]`, 'gu');
  let inputText = '';

  // creating raw value
  if (inputValue) {
    inputText = inputValue.trim().replace(patternGroup, '').replace(patternDecimal, '.').replace(/^\u200e/, '').replace(/\u2212/, '-');
  }
  const formattedInput = formatter ? formatter.formatNumeric(Number(inputText) + 0, undefined, numberFormatOptions) : defaultFormatter.format(parseFloat(inputText) + 0); // -0 + 0 = 0;
  return {
    rawValue: parseFloat(inputText) + 0,
    formatValue: formattedInput
  };
};
export const getNumberSeparators = (locale = 'en-US') => {
  const decimalSeparator = 1.1.toLocaleString(locale).replace(/1/gu, '');
  let groupingSeparator = 11111 .toLocaleString(locale).replace(/1/gu, '');
  const sSpaceRegex = /^\s$/;

  // if grouping separator is no-break space (French) then replace the space characters from user input with no-break space
  if (sSpaceRegex.test(groupingSeparator)) {
    groupingSeparator = '\\s';
  }
  return [decimalSeparator, groupingSeparator];
};
/**
 *
 * @param inputValue
 * @param applocale
 * @param isLive
 *
 * This function is to validate if a string is a valid number format
 * @returns invalid boolean flag
 */
export const checkValidNumberFormat = (inputValue, applocale, isLive) => {
  const defaultLocale = getDefaultLocale(applocale);
  const numberFormat = new Intl.NumberFormat(defaultLocale);
  const [decimalSeparator, groupingSeparator] = getNumberSeparators(numberFormat.resolvedOptions().locale);
  if (inputValue.length >= 2 && inputValue.charAt(0) === '0' && (inputValue.charAt(1) === groupingSeparator || groupingSeparator === '\\s' && inputValue.charAt(1) === ' ')) {
    return false;
  }
  const escapedSeparator = groupingSeparator === '\\s' ? groupingSeparator : groupingSeparator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const numberValidationTemplate = numberFormat.format(100000);
  const groupingSeparators = numberValidationTemplate.match(new RegExp(escapedSeparator, 'g'));
  const countGroupingSeperators = groupingSeparators ? groupingSeparators.length : 0;
  const isThreeDigitGroupingFormat = countGroupingSeperators === 1 ? true : false;

  // if the value is in editing, the '4{groupingSeparator}4' is valid
  const regEx = isLive ? isThreeDigitGroupingFormat ? `^\\u200e?[\\u2212+-]?((\\d+|([1-9]\\d{0,2}|\\d{0,2}[1-9])([${groupingSeparator}]\\d{3})*)?([${decimalSeparator}]\\d*)?|(([1-9]\\d{0,2}|\\d{0,2}[1-9])(([${groupingSeparator}]\\d{3})*)?)([${groupingSeparator}]\\d{0,3})?)?$` : `^\\u200e?[\\u2212+-]?((\\d+|([1-9]\\d{0,1}|\\d{0,1}[1-9])([${groupingSeparator}]\\d{2})*[${groupingSeparator}]\\d{3})?([${decimalSeparator}]\\d*)?|(([1-9]\\d{0,1}|\\d{0,1}[1-9])(([${groupingSeparator}]\\d{2})*)?)([${groupingSeparator}]\\d{0,3})?)?$` : isThreeDigitGroupingFormat ? `^\\u200e?[\\u2212+-]?((\\d+|([1-9]\\d{0,2}|\\d{0,2}[1-9])([${groupingSeparator}]\\d{3})*)([${decimalSeparator}]\\d+)?|([${decimalSeparator}]\\d+))$` : `^\\u200e?[\\u2212+-]?((\\d+|([1-9]\\d{0,1}|\\d{0,1}[1-9])((([${groupingSeparator}]\\d{2})+([${groupingSeparator}]\\d{3}))|([${groupingSeparator}]\\d{3})?))([${decimalSeparator}]\\d+)?|([${decimalSeparator}]\\d+))$`;
  return new RegExp(regEx).test(inputValue);
};
export const isValidChars = (inputValue, isLive = false, locale = 'en-US', allowIntegersOnly = false) => {
  const [decimalSeparator] = getNumberSeparators(locale);
  const validCharsReExp = `^[\u2212+-]?${allowIntegersOnly ? '' : `[${decimalSeparator}]?`}$`;
  return new RegExp(validCharsReExp).test(inputValue) && isLive;
};
const hasDecimalSeparator = (inputValue, locale = 'en-US') => {
  const [decimalSeparator] = getNumberSeparators(locale);
  const decimalRegExp = `\\${decimalSeparator}`;
  return new RegExp(decimalRegExp).test(inputValue);
};

/**
 *
 * @param value
 * @param appLocale
 * @param isRequired
 * @param allowIntegersOnly
 * @param isLive
 * @param minLength
 * @param numberFormatOptions
 * @param parseAndFormatFunction
 * @param formatter
 *
 *
 * This function is used for validating input based on passed params.
 * @returns values{rawValue, formattedValue}, isError, status, message
 */
export const validateNumericInput = (value, appLocale, isRequired, allowIntegersOnly, isLive, minLength, numberFormatOptions, parseAndFormatFunction, formatter, localizer) => {
  if (!localizer) {
    localizer = localize;
  }
  let values;
  if (parseAndFormatFunction) {
    values = parseAndFormatFunction(value);
  } else {
    values = parseAndFormatInput(value, appLocale, numberFormatOptions, formatter, isLive);
  }
  let isError = false,
    status,
    message;
  if (value) {
    if (isNaN(values.rawValue) && !isValidChars(value, isLive, appLocale)) {
      isError = true;
      status = 'alert';
      message = allowIntegersOnly ? localizer('numericValidator.integers.error.msg') : localizer('numericValidator.numeric.error.msg');
    } else if (allowIntegersOnly && hasDecimalSeparator(value, appLocale)) {
      isError = true;
      status = 'alert';
      message = localizer('numericValidator.integers.error.msg');
    } else if (minLength && value.length < minLength && !isLive) {
      const minLen = minLength.toString();
      isError = true;
      status = 'alert';
      message = localizer('minlength.error.msg', {
        minlength: minLen
      });
    }
  } else if (isRequired && !isLive) {
    isError = true;
    status = 'alert';
    message = localizer('required.error.msg');
  }
  return {
    isError,
    status,
    message,
    values
  };
};
//# sourceMappingURL=useInputValidationAdapter.js.map